import React, { useState } from 'react';

import { Button, Empty, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import ConfirmModal from 'common/Modal/ConfirmModal';
import Input from 'components/lib/Input';
import { RegisteredTaskType, TASK_CONTROL_ACTIONS, TaskControlActionType } from 'types/tasks';

import { CenteredState, StatusBadge, TabContent, TaskName, TaskQueueTabHeader } from './TaskQueueTabContent';

const TaskCountBadge = styled(StatusBadge)`
  color: var(--text-secondary);
  background: var(--lighter-gray);
`;

const TaskControlsContent = styled(TabContent)`
  gap: 20px;

  .ant-table-tbody > tr:nth-child(odd),
  .ant-table-tbody > tr:nth-child(even) {
    background-color: transparent;
  }
`;

const TaskSection = styled.section`
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  background: var(--white);
`;

const TaskSectionHeader = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid var(--line-gray);
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TaskActionButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  line-height: 1;
`;

const TaskFilterContainer = styled.div`
  width: 320px;
`;

type Props = {
  canManageTasks: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  onChangeRevocation: (taskName: string, action: TaskControlActionType) => Promise<void>;
  tasks: RegisteredTaskType[];
};

const TaskControlsTab = ({ canManageTasks, isLoading, isUpdating, onChangeRevocation, tasks }: Props) => {
  const [selectedTask, setSelectedTask] = useState<RegisteredTaskType | null>(null);
  const [isRunAction, setIsRunAction] = useState(false);
  const selectTask = (task: RegisteredTaskType, run = false) => {
    setSelectedTask(task);
    setIsRunAction(run);
  };
  const [taskNameFilter, setTaskNameFilter] = useState('');
  const selectedAction = isRunAction
    ? TASK_CONTROL_ACTIONS.RUN
    : selectedTask?.is_revoked
      ? TASK_CONTROL_ACTIONS.RESTORE
      : TASK_CONTROL_ACTIONS.REVOKE;
  const selectedActionLabel = isRunAction ? 'Run' : selectedTask?.is_revoked ? 'Restore' : 'Revoke';
  const confirmationDescription = selectedTask
    ? isRunAction
      ? `Queue ${selectedTask.name} to run once now? Its periodic schedule will remain unchanged. Existing disable flags and duplicate-task controls still apply.`
      : selectedTask.is_revoked
        ? `Restore ${selectedTask.name}? Future queued instances will be allowed to execute.`
        : `Revoke ${selectedTask.name}? Queued and future instances will be skipped. A task already executing will continue.`
    : '';
  const normalizedTaskNameFilter = taskNameFilter.trim().toLowerCase();
  const sortedTasks = [...tasks].sort((firstTask, secondTask) =>
    firstTask.name.localeCompare(secondTask.name, undefined, { sensitivity: 'base' })
  );
  const filteredTasks = normalizedTaskNameFilter
    ? sortedTasks.filter((task) => task.name.toLowerCase().includes(normalizedTaskNameFilter))
    : sortedTasks;
  const revokedTasks = sortedTasks.filter((task) => task.is_revoked);

  const revokedColumns: ColumnsType<RegisteredTaskType> = [
    {
      title: 'Task type',
      dataIndex: 'name',
      key: 'name',
      width: canManageTasks ? '65%' : '80%',
      render: (name) => <TaskName>{name}</TaskName>,
    },
    {
      title: 'Status',
      key: 'status',
      width: '20%',
      render: () => <StatusBadge error>Revoked</StatusBadge>,
    },
  ];

  if (canManageTasks) {
    revokedColumns.push({
      title: 'Action',
      key: 'action',
      align: 'right',
      width: '15%',
      render: (_, task) => (
        <Button
          data-test-id={`task-queue-restore-revoked-${task.name}`}
          disabled={isUpdating}
          size="small"
          onClick={() => selectTask(task)}
        >
          Restore
        </Button>
      ),
    });
  }

  const handleConfirm = async () => {
    if (!selectedTask) {
      return;
    }

    try {
      await onChangeRevocation(selectedTask.name, selectedAction);
      setSelectedTask(null);
    } catch {
      // Mutation feedback is handled by the parent; keep the modal open for retry.
    }
  };

  const columns: ColumnsType<RegisteredTaskType> = [
    {
      title: 'Task type',
      dataIndex: 'name',
      key: 'name',
      width: canManageTasks ? '65%' : '80%',
      render: (name) => <TaskName>{name}</TaskName>,
    },
    {
      title: 'Status',
      dataIndex: 'is_revoked',
      key: 'is_revoked',
      width: '20%',
      render: (isRevoked) =>
        isRevoked ? <StatusBadge error>Revoked</StatusBadge> : <StatusBadge>Enabled</StatusBadge>,
    },
  ];

  if (canManageTasks) {
    columns.push({
      title: 'Action',
      key: 'action',
      align: 'right',
      width: '15%',
      render: (_, task) => {
        const actionLabel = task.is_revoked ? 'Restore' : 'Revoke';

        return (
          <SectionActions>
            <TaskActionButton
              data-test-id={`task-queue-revocation-${task.name}`}
              danger={!task.is_revoked}
              disabled={isUpdating}
              size="small"
              onClick={() => selectTask(task)}
            >
              {actionLabel}
            </TaskActionButton>
            {task.is_periodic && (
              <TaskActionButton
                type="primary"
                data-test-id={`task-queue-run-${task.name}`}
                disabled={isUpdating || task.is_revoked}
                size="small"
                onClick={() => selectTask(task, true)}
              >
                Run
              </TaskActionButton>
            )}
          </SectionActions>
        );
      },
    });
  }

  return (
    <TaskControlsContent>
      <TaskSection>
        <TaskSectionHeader>
          <TaskQueueTabHeader
            title="Revoked tasks"
            description="Task types Huey will skip until they are restored."
            action={<TaskCountBadge>{revokedTasks.length.toLocaleString()} revoked</TaskCountBadge>}
          />
        </TaskSectionHeader>
        {isLoading ? (
          <CenteredState>
            <Spin />
          </CenteredState>
        ) : (
          <Table
            rowKey="name"
            columns={revokedColumns}
            dataSource={revokedTasks}
            pagination={false}
            locale={{ emptyText: <Empty description="No registered tasks are revoked" /> }}
          />
        )}
      </TaskSection>
      <TaskSection>
        <TaskSectionHeader>
          <TaskQueueTabHeader
            title="All tasks"
            description={
              canManageTasks
                ? 'Run periodic tasks once, or revoke and restore registered task types.'
                : 'Registered Huey task types and their current revocation state. Controls are restricted.'
            }
            action={
              <SectionActions>
                <TaskFilterContainer>
                  <Input
                    data-test-id="task-queue-filter"
                    allowClear
                    aria-label="Filter tasks by name"
                    placeholder="Filter by task name"
                    value={taskNameFilter}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTaskNameFilter(event.target.value)}
                  />
                </TaskFilterContainer>
                <TaskCountBadge>
                  {filteredTasks.length.toLocaleString()}
                  {normalizedTaskNameFilter ? ` of ${sortedTasks.length.toLocaleString()}` : ''} tasks
                </TaskCountBadge>
              </SectionActions>
            }
          />
        </TaskSectionHeader>
        {isLoading ? (
          <CenteredState>
            <Spin />
          </CenteredState>
        ) : (
          <Table
            rowKey="name"
            columns={columns}
            dataSource={filteredTasks}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  description={taskNameFilter ? 'No task types match this name' : 'No registered task types found'}
                />
              ),
            }}
          />
        )}
      </TaskSection>
      <ConfirmModal
        open={Boolean(selectedTask)}
        title={`${selectedActionLabel} task type`}
        description={confirmationDescription}
        loading={isUpdating}
        confirmLabel={selectedActionLabel}
        confirmButtonDataTestId="task-queue-control-confirm"
        cancelButtonDataTestId="task-queue-control-cancel"
        onConfirm={handleConfirm}
        onCancel={() => !isUpdating && setSelectedTask(null)}
      />
    </TaskControlsContent>
  );
};

export default TaskControlsTab;
